/**
 * Constants and utility functions for KYC approvals
 */

// ==================== Section Keys ====================

export const SECTION_KEYS = {
  KYC: 'kyc',
  FUNCTIONS: 'functions',
  ASSIGNMENTS: 'assignments',
  PERMISSIONS: 'permissions',
  PAYOUT: 'payout',
  OPS: 'ops',
}

export const ALL_SECTIONS = Object.values(SECTION_KEYS)

// ==================== Status Enums ====================

export const OVERALL_STATUS = {
  REQUESTED: 'REQUESTED',
  IN_REVIEW: 'IN_REVIEW',
  PARTIALLY_APPROVED: 'PARTIALLY_APPROVED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
}

export const SECTION_STATUS = {
  EMPTY: 'EMPTY',
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
}

export const KYC_STATUS = {
  APPROVED: 'APPROVED',
  PENDING: 'PENDING',
  REJECTED: 'REJECTED',
}

export const ACCESS_MODE = {
  REMOTE: 'REMOTE',
  IN_PERSON: 'IN_PERSON',
  HYBRID: 'HYBRID',
}

export const PATIENT_VISIBILITY = {
  ALL_IN_ASSIGNED_CLINICS: 'ALL_IN_ASSIGNED_CLINICS',
  ONLY_OWN_BOOKINGS: 'ONLY_OWN_BOOKINGS',
  ONLY_EXPLICIT_ASSIGNMENTS: 'ONLY_EXPLICIT_ASSIGNMENTS',
}

export const PAYOUT_MODEL = {
  RETAINER_PLUS_SHARE: 'RETAINER_PLUS_SHARE',
  PURE_SHARE: 'PURE_SHARE',
  PER_SESSION: 'PER_SESSION',
}

export const PAYOUT_METHOD = {
  BANK_TRANSFER: 'BANK_TRANSFER',
  UPI: 'UPI',
  CHEQUE: 'CHEQUE',
}

// ==================== Section Configuration ====================

export const SECTION_CONFIG = [
  { key: SECTION_KEYS.KYC, label: 'Approval', icon: 'cil-check-circle' },
  { key: SECTION_KEYS.FUNCTIONS, label: 'Functions', icon: 'cil-layers' },
  { key: SECTION_KEYS.ASSIGNMENTS, label: 'Assignments', icon: 'cil-location-pin' },
  { key: SECTION_KEYS.PERMISSIONS, label: 'Permissions', icon: 'cil-lock-locked' },
  { key: SECTION_KEYS.PAYOUT, label: 'Payouts', icon: 'cil-credit-card' },
  { key: SECTION_KEYS.OPS, label: 'Ops', icon: 'cil-settings' },
]

// ==================== Status Display Helpers ====================

/**
 * Get display color for overall status
 */
export const getOverallStatusColor = (status) => {
  switch (status) {
    case OVERALL_STATUS.APPROVED:
      return 'success'
    case OVERALL_STATUS.REJECTED:
      return 'error'
    case OVERALL_STATUS.IN_REVIEW:
    case OVERALL_STATUS.PARTIALLY_APPROVED:
      return 'warning'
    case OVERALL_STATUS.REQUESTED:
    default:
      return 'info'
  }
}

/**
 * Get display color for section status
 */
export const getSectionStatusColor = (status) => {
  switch (status) {
    case SECTION_STATUS.APPROVED:
      return 'success'
    case SECTION_STATUS.REJECTED:
      return 'error'
    case SECTION_STATUS.SUBMITTED:
      return 'warning'
    case SECTION_STATUS.DRAFT:
      return 'info'
    case SECTION_STATUS.EMPTY:
    default:
      return 'default'
  }
}

/**
 * Get human-readable status label
 */
export const getStatusLabel = (status) => {
  if (!status) return 'Unknown'
  return status
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')
}

// ==================== Validation Helpers ====================

/**
 * Check if section has data (not empty)
 */
export const hasSectionData = (sectionData) => {
  if (!sectionData) return false
  if (Array.isArray(sectionData)) return sectionData.length > 0
  if (typeof sectionData === 'object') return Object.keys(sectionData).length > 0
  return true
}

/**
 * Validate KYC section data
 */
export const validateKycSection = (data) => {
  if (!data) return { valid: false, error: 'KYC data is required' }
  if (!data.status) return { valid: false, error: 'KYC status is required' }
  if (!data.checklist) return { valid: false, error: 'Document checklist is required' }
  return { valid: true }
}

/**
 * Validate Functions section data
 */
export const validateFunctionsSection = (data) => {
  if (!data) return { valid: false, error: 'Functions data is required' }

  const { screening, therapy, consultant } = data

  // At least one function must be enabled
  if (!screening?.enabled && !therapy?.enabled && !consultant?.enabled) {
    return { valid: false, error: 'At least one function must be enabled' }
  }

  // If screening enabled, must have modules
  if (screening?.enabled && (!screening.modules || screening.modules.length === 0)) {
    return { valid: false, error: 'Screening modules are required when screening is enabled' }
  }

  // If therapy enabled, must have types
  if (therapy?.enabled && (!therapy.types || therapy.types.length === 0)) {
    return { valid: false, error: 'Therapy types are required when therapy is enabled' }
  }

  return { valid: true }
}

/**
 * Validate Assignments section data
 */
export const validateAssignmentsSection = (data) => {
  if (!data || !data.assignments || data.assignments.length === 0) {
    return { valid: false, error: 'At least one brand assignment is required' }
  }

  for (const assignment of data.assignments) {
    if (!assignment.brandId) {
      return { valid: false, error: 'Brand ID is required for all assignments' }
    }
    if (!assignment.locations || assignment.locations.length === 0) {
      return { valid: false, error: 'At least one location is required per brand' }
    }
    for (const location of assignment.locations) {
      if (!location.locationId) {
        return { valid: false, error: 'Location ID is required' }
      }
      if (!location.accessMode) {
        return { valid: false, error: 'Access mode is required for all locations' }
      }
    }
  }

  return { valid: true }
}

/**
 * Validate Permissions section data
 */
export const validatePermissionsSection = (data) => {
  if (!data) return { valid: false, error: 'Permissions data is required' }
  if (!data.patientVisibility) {
    return { valid: false, error: 'Patient visibility setting is required' }
  }
  return { valid: true }
}

/**
 * Validate Payout section data
 */
export const validatePayoutSection = (data) => {
  if (!data) return { valid: false, error: 'Payout data is required' }
  if (!data.model) return { valid: false, error: 'Payout model is required' }

  // Validate based on model
  if (data.model === PAYOUT_MODEL.RETAINER_PLUS_SHARE) {
    if (!data.retainerAmount) return { valid: false, error: 'Retainer amount is required' }
    if (!data.shareFirstVisit) return { valid: false, error: 'First visit share % is required' }
    if (!data.shareFollowUp) return { valid: false, error: 'Follow-up share % is required' }
  } else if (data.model === PAYOUT_MODEL.PURE_SHARE) {
    if (!data.shareFirstVisit) return { valid: false, error: 'First visit share % is required' }
    if (!data.shareFollowUp) return { valid: false, error: 'Follow-up share % is required' }
  } else if (data.model === PAYOUT_MODEL.PER_SESSION) {
    if (!data.perSessionRate) return { valid: false, error: 'Per session rate is required' }
  }

  // Validate banking details
  if (!data.banking) return { valid: false, error: 'Banking details are required' }
  const { banking } = data
  if (!banking.accountHolderName) return { valid: false, error: 'Account holder name is required' }
  if (!banking.accountNumber) return { valid: false, error: 'Account number is required' }
  if (!banking.ifsc) return { valid: false, error: 'IFSC code is required' }
  if (!banking.bankName) return { valid: false, error: 'Bank name is required' }

  return { valid: true }
}

/**
 * Validate Ops section data
 */
export const validateOpsSection = (data) => {
  if (!data) return { valid: false, error: 'Ops data is required' }
  // Ops section has no required fields, all are toggles
  return { valid: true }
}

/**
 * Validate all sections before save & approve
 */
export const validateAllSections = (formData, visibleSections) => {
  const validators = {
    kyc: validateKycSection,
    functions: validateFunctionsSection,
    assignments: validateAssignmentsSection,
    permissions: validatePermissionsSection,
    payout: validatePayoutSection,
    ops: validateOpsSection,
  }

  for (const sectionKey of visibleSections) {
    const validator = validators[sectionKey]
    if (!validator) continue

    const result = validator(formData[sectionKey])
    if (!result.valid) {
      return { valid: false, error: result.error, section: sectionKey }
    }
  }

  return { valid: true }
}

// ==================== Mock Data (for chambers until API is ready) ====================

export const MOCK_CHAMBERS = [
  { id: 'chamber-1', name: 'Room 101' },
  { id: 'chamber-2', name: 'Room 102' },
  { id: 'chamber-3', name: 'Room 103' },
  { id: 'chamber-4', name: 'Virtual A' },
  { id: 'chamber-5', name: 'Virtual B' },
]

// ==================== Empty State Generators ====================

/**
 * Get empty form data for a new request
 */
export const getEmptyFormData = () => ({
  kyc: {
    status: KYC_STATUS.PENDING,
    notes: '',
    checklist: {
      govtId: false,
      degreeCert: false,
      councilReg: false,
      addressProof: false,
    },
    documents: {
      govtIdUrls: [],
      degreeCertificateUrls: [],
      councilRegistrationUrls: [],
      addressProofUrls: [],
    },
  },
  functions: {
    screening: { enabled: false, modules: [] },
    therapy: { enabled: false, types: [] },
    consultant: { enabled: false, feeSlab: '' },
  },
  assignments: {
    assignments: [],
  },
  permissions: {
    patientVisibility: PATIENT_VISIBILITY.ONLY_OWN_BOOKINGS,
    canViewNonAssignedEmr: false,
    canInitiateFollowups: false,
    canSeeBilling: false,
    canAccessSupportLogs: false,
  },
  payout: {
    model: PAYOUT_MODEL.RETAINER_PLUS_SHARE,
    retainerAmount: null,
    shareFirstVisit: null,
    shareFollowUp: null,
    perSessionRate: null,
    notes: '',
    banking: {
      accountHolderName: '',
      accountNumber: '',
      ifsc: '',
      bankName: '',
      branch: '',
      upiId: '',
      pan: '',
      gstin: '',
      payoutMethod: PAYOUT_METHOD.BANK_TRANSFER,
      cancelledChequeUrl: '',
    },
  },
  ops: {
    canEditTherapyNotes: true,
    canSendWhatsapp: true,
    canMarkNoShow: true,
    canRequestAssessment: true,
    canIssueInvoice: false,
    publicDirectory: false,
    addToPeerLearning: true,
  },
})
